<script lang="ts">
  export * from './Metric.types';
</script>

<script setup lang="ts">
  import uniqueId from 'lodash-es/uniqueId';
  import { computed, ComputedRef, useSlots } from 'vue';

  import { StashCommonColor } from '../../../types/colors';
  import { MetricSize, MetricSizes, MetricVariant, MetricVariants } from './Metric.types';

  export interface MetricProps {
    /**
     * The primary text that will show in the metric.
     */
    value: number | string;

    /**
     * The label for the metric. Appears above the value.
     */
    label: string;

    /**
     * The variant of the metric. Defaults to `naked`.
     */
    variant?: MetricVariant;

    /**
     * The accent color for the metric. Defaults to `teal`. Currently only used when the variant = `accent`.
     */
    accentColor?: StashCommonColor;

    /**
     * Controls the size of the metric via text size adjustments.
     */
    size?: MetricSize;
  }

  const props = withDefaults(defineProps<MetricProps>(), {
    size: 'standard',
    variant: 'naked',
    accentColor: 'teal-500',
  });

  const slots = useSlots();

  const fieldId = computed(() => uniqueId('metric-'));

  const computedValue: ComputedRef<string> = computed(() => String(props.value));
</script>

<template>
  <div
    class="stash-metric"
    data-test="stash-metric"
    :class="[
      `stash-metric--${props.size}`,
      {
        'stash-metric--naked': props.variant === MetricVariants.Naked,
        [`stash-metric--accent border-${accentColor} rounded border-l-12 bg-white p-3 shadow`]:
          props.variant === MetricVariants.Accent,
      },
    ]"
  >
    <div
      :id="fieldId"
      class="stash-metric__label mb-1 text-ice-700"
      data-test="stash-metric|label"
      :class="[
        {
          'text-xs': props.size === MetricSizes.Small,
          'text-sm': props.size === MetricSizes.Standard,
        },
      ]"
    >
      {{ props.label }}
    </div>

    <div
      :aria-labelledby="fieldId"
      class="stash-metric__value font-medium text-ice-900"
      data-test="stash-metric|value"
      :class="{
        'text-base': props.size === MetricSizes.Small,
        'text-2xl': props.size === MetricSizes.Standard,
      }"
    >
      {{ computedValue }}
    </div>

    <div
      v-if="slots.secondary"
      class="stash-metric__secondary mt-1 text-xs text-ice-700"
      data-test="stash-metric|secondary"
    >
      <!-- @slot Supplementary information like a date, status, hint text, or secondary label. -->
      <slot name="secondary"></slot>
    </div>
  </div>
</template>
